/**
 * Layout pieces every console page shares, so the pages agree with each other.
 *
 * A page header is one thing — title, one sentence, actions on the right — and a page that does
 * its own gets it subtly wrong: a different weight, a different gap, a description that wraps at a
 * different width. The compact density (ux.md §1: 4 px grid, 32 px rows) is what these encode.
 *
 * Everything here styles from the SEMANTIC token layer, elevation and easing included, so a card
 * lifts the same amount on every screen and does the right thing in dark mode without knowing dark
 * mode exists. `shadow-raised`/`shadow-lifted`/`ease-standard` are the three names to reach for;
 * a hand-rolled `shadow-[0_2px_8px_…]` is the thing they exist to stop.
 */
import type { ReactNode } from 'react'
import { Icon, type IconName } from './icons'

export type PageHeaderProps = {
  readonly title: string
  /** One sentence under the title. Kept to a measure a person can read in one pass. */
  readonly description?: ReactNode
  /** Small text above the title: the section this page belongs to. */
  readonly eyebrow?: string
  /** Controls on the right — a primary action, a filter. */
  readonly actions?: ReactNode
  /**
   * `lg` for a landing page, `md` (the default) for a working screen.
   *
   * One knob rather than a second component, and it is deliberately not free-form: a dashboard is
   * arrived at, so its heading may take the room, while a list somebody reaches twenty times a day
   * should spend that room on rows. Two sizes is the whole vocabulary — a third would be somebody
   * splitting the difference.
   */
  readonly size?: 'md' | 'lg'
}

export function PageHeader({ title, description, eyebrow, actions, size = 'md' }: PageHeaderProps) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
      <div className="min-w-0">
        {eyebrow ? (
          <p className="text-text-muted m-0 mb-1 text-xs font-medium uppercase tracking-wide">
            {eyebrow}
          </p>
        ) : null}
        <h1
          className={`text-text-primary m-0 font-semibold tracking-tight ${
            size === 'lg' ? 'text-3xl' : 'text-2xl'
          }`}
        >
          {title}
        </h1>
        {description ? (
          <div className={`text-text-muted mt-1.5 max-w-2xl ${size === 'lg' ? 'text-lg' : ''}`}>
            {description}
          </div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  )
}

export type SectionHeadingProps = {
  readonly title: string
  /** The id the section's `aria-labelledby` points at. */
  readonly id?: string
  /** One clause under the title, saying what the section contains rather than repeating its name. */
  readonly description?: string
  /** A "View all" or a filter, on the right, on the title's baseline. */
  readonly action?: ReactNode
}

/**
 * The heading over one band of a page: title, one clause, and the way out of it.
 *
 * Exists because "a heading and a link on the right" was being rebuilt per section with a
 * different gap and a different weight each time, and a dashboard is mostly headings — the
 * inconsistency is the first thing a reader notices and the last thing anyone writes down.
 */
export function SectionHeading({ title, id, description, action }: SectionHeadingProps) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
      <div className="min-w-0">
        <h2 id={id} className="text-text-primary m-0 text-base font-semibold tracking-tight">
          {title}
        </h2>
        {description ? <p className="text-text-muted mb-0 mt-0.5 text-sm">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0 text-sm font-medium">{action}</div> : null}
    </div>
  )
}

export type KbdProps = { readonly children: ReactNode; readonly className?: string }

/** A keyboard shortcut, drawn as a key. */
export function Kbd({ children, className = '' }: KbdProps) {
  return (
    <kbd
      className={`border-border-subtle bg-surface-raised text-text-muted inline-flex h-5 min-w-5 items-center justify-center rounded-sm border px-1 font-sans text-xs ${className}`}
    >
      {children}
    </kbd>
  )
}

/**
 * The tones a tinted icon chip comes in, shared by StatTile, ActionCard and Pipeline.
 *
 * `danger` was missing for a long time and its absence was doing real damage: anything genuinely
 * WRONG — a declined envelope, an expired one — had to wear `warning` amber, which understates it,
 * or `neutral`, which hides it. `Meter` in this same file already carried a danger tone, so the
 * omission was an oversight rather than a decision. No new colour: `--danger` is a semantic token
 * that has shipped since the first screens, and `bg-danger/12` is the exact idiom `Badge` already
 * uses, so `lint:design-tokens` sees a name it already knows (the gate strips the `/NN` opacity
 * modifier before looking a token up).
 */
export type Tone = 'neutral' | 'accent' | 'warning' | 'success' | 'danger'

const CHIP_TONE: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-text-muted',
  accent: 'bg-accent/10 text-accent',
  warning: 'bg-warning/12 text-warning',
  success: 'bg-success/12 text-success',
  danger: 'bg-danger/12 text-danger',
}

export type StatTileProps = {
  readonly label: string
  /** The figure. `null` renders an honest dash: the number is not known, which is not zero. */
  readonly value: number | string | null
  readonly icon?: IconName
  /** One short clause under the figure. */
  readonly hint?: string
  readonly tone?: Tone
  /**
   * A full-card click target, if the figure leads somewhere.
   *
   * The caller passes its own router link with `className="absolute inset-0"` and an `aria-label`
   * naming the destination — the stretched-link pattern — which keeps this component
   * router-agnostic while making the whole tile, not a 14 px chevron, the hit area. The tile grows
   * a chevron and its hover lift only when this is present, so a tile that goes nowhere never
   * pretends otherwise.
   */
  readonly link?: ReactNode
}

/** A dashboard figure: label, number, one clause. Zone 1 and 2 of the dashboard (ux.md §6.1). */
export function StatTile({ label, value, icon, hint, tone = 'neutral', link }: StatTileProps) {
  const interactive = link !== undefined
  return (
    <div
      className={`border-card-border bg-card-bg shadow-raised ease-standard focus-within:border-accent group relative flex items-start gap-3 rounded-xl border p-4 transition duration-150 ${
        interactive
          ? 'hover:border-border-strong hover:shadow-lifted motion-safe:hover:-translate-y-0.5'
          : ''
      }`}
    >
      {icon ? (
        <span
          className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${CHIP_TONE[tone]}`}
        >
          <Icon name={icon} size={18} />
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="text-text-muted m-0 text-sm font-medium">{label}</p>
        {/* `tabular-nums` so a tile that ticks from 9 to 10 does not shuffle the ones beside it. */}
        <p className="text-text-primary m-0 mt-0.5 text-3xl font-semibold tabular-nums leading-none tracking-tight">
          {value === null ? (
            <>
              <span aria-hidden="true">—</span>
              <span className="visually-hidden">not yet available</span>
            </>
          ) : (
            value
          )}
        </p>
        {hint ? <p className="text-text-muted m-0 mt-1.5 text-xs">{hint}</p> : null}
      </div>
      {interactive ? (
        <>
          <Icon
            name="chevron-right"
            size={16}
            className="text-text-muted ease-standard mt-0.5 transition-transform duration-150 motion-safe:group-hover:translate-x-0.5"
          />
          {link}
        </>
      ) : null}
    </div>
  )
}

export type PipelineStage = {
  /** Stable key for React, and what a test scopes an assertion to. */
  readonly key: string
  readonly label: string
  /** The figure. `null` renders an honest dash — the number is not known, which is not zero. */
  readonly value: number | null
  /** One short clause under the figure, saying which envelopes it counted. */
  readonly hint: string
  readonly tone: Tone
  /**
   * A full-card click target, if the stage leads somewhere.
   *
   * Same stretched-link contract as StatTile: the caller passes its own router link with
   * `className="absolute inset-0"` and an `aria-label` naming the destination, which keeps this
   * component router-agnostic while making the whole legend entry the hit area.
   */
  readonly link?: ReactNode
}

export type PipelineProps = {
  readonly stages: readonly PipelineStage[]
  /** The sentence under the bar. Always shown, because what was counted is part of the figure. */
  readonly caption?: ReactNode
}

/**
 * The segment fills, and why they are not `bg-warning` and `bg-success`.
 *
 * They were, and it was measurably wrong in light mode. `--warning` is `#8a5a00` and `--success` is
 * `#1a6b3c`, and both are dark BECAUSE they are text colours — they carry 4.5:1 against a white
 * page, which is what a status word needs and what a 12 px band of solid colour does not. Rendered
 * as areas they read as brown and bottle green: the bar looked muddy in light and, because the dark
 * theme's `-400` variants are chosen for a near-black ground, vivid in dark. One bar, two different
 * products, and the light one was the one customers see first.
 *
 * The chart series tokens are the palette this design system already reserves for AREAS. They are
 * validated as a set in both themes with the data-viz checks — lightness band, chroma floor,
 * adjacent-pair separation under a red-green deficiency, contrast against the card surface — which
 * is exactly the property four abutting segments need and exactly what the semantic text tokens
 * were never chosen for. `--chart-expired` is the neutral of that set, so drafts take it rather
 * than a border colour pressed into service as a fill.
 *
 * The accent stays the accent: the actionable stage should be the colour of every other actionable
 * thing in the console, and it follows a tenant's brand where the other three deliberately do not.
 */
const PIPELINE_FILL: Record<Tone, string> = {
  neutral: 'bg-chart-expired',
  accent: 'bg-accent',
  warning: 'bg-chart-in-progress',
  success: 'bg-chart-completed',
  danger: 'bg-chart-declined',
}

/** The legend dot is the segment's colour, because it is what maps a figure to a band. */
const PIPELINE_DOT = PIPELINE_FILL

/**
 * One bar over a partition, and the doors into its parts.
 *
 * ## Why a bar and not four tiles
 *
 * The four figures on this screen are not four metrics. They are one set — every envelope the
 * reader can see that has not been voided — cut four ways by `bucketEnvelopeCounts`, and the cuts
 * are exhaustive and disjoint. Four tiles throw that away: they show four numbers of equal weight
 * and leave the reader to notice, or not, that the numbers add up to something. A proportional bar
 * shows the SHAPE — mostly waiting, or mostly stuck, or nothing sent yet — before a single figure
 * is read, which is the question somebody opening a signing console actually has.
 *
 * It is also the shape our competitors do not draw. The category standard is a grid of KPI cards,
 * each with a big number, a percentage delta and a sparkline; DocuSign's ships with the sparklines
 * captioned EXAMPLE DATA, which is what happens when the chart is decoration rather than
 * information. There is no trend line here because this product has never measured a trend on this
 * screen, and drawing one would be the same lie in our own colours.
 *
 * ## The three states, and why they must look different
 *
 * MEASURED — segments, proportional, one per non-zero stage.
 *
 * EMPTY — every stage zero: a plain track. Nothing has been sent, which is a fact.
 *
 * UNKNOWN — any stage `null`, meaning the read failed: a DASHED track, and every figure a dash.
 * A bar that drew "empty" for a count it could not load would tell somebody with nine envelopes
 * out for signature that they have none, which is the most reassuring possible lie (ux.md §6.1)
 * and the reason the tiles have always shown a dash rather than a nought.
 *
 * The bar itself is `aria-hidden`: it is a second rendering of figures the legend below states in
 * words, and a screen reader that read both would read every number twice.
 */
export function Pipeline({ stages, caption }: PipelineProps) {
  const unknown = stages.some((stage) => stage.value === null)
  const total = unknown ? 0 : stages.reduce((sum, stage) => sum + (stage.value ?? 0), 0)

  return (
    <div>
      {unknown || total === 0 ? (
        <div
          aria-hidden="true"
          className={`bg-surface-sunken h-3 w-full rounded-full ${
            unknown ? 'border-border-strong border border-dashed' : ''
          }`}
        />
      ) : (
        <div aria-hidden="true" className="flex h-3 w-full gap-1">
          {stages
            .filter((stage) => (stage.value ?? 0) > 0)
            .map((stage) => (
              <div
                key={stage.key}
                // `flexGrow` rather than a width percentage, with a floor: a stage holding one of
                // two hundred envelopes is 0.5% of the bar, which rounds to nothing on any screen
                // — and a segment that disappears is a stage the reader believes is empty.
                style={{ flexGrow: stage.value ?? 0 }}
                className={`ease-standard h-3 min-w-1.5 rounded-full transition-[flex-grow] duration-500 ${
                  PIPELINE_FILL[stage.tone]
                }`}
              />
            ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4 lg:grid-cols-4">
        {stages.map((stage) => {
          const interactive = stage.link !== undefined
          return (
            <div
              key={stage.key}
              className={`focus-within:border-accent group relative -mx-2 rounded-lg px-2 py-1.5 transition-colors duration-150 ${
                interactive ? 'hover:bg-surface-sunken/70 border border-transparent' : ''
              }`}
            >
              <p className="text-text-muted m-0 flex items-center gap-2 text-sm font-medium">
                <span
                  aria-hidden="true"
                  className={`size-2.5 shrink-0 rounded-full ${PIPELINE_DOT[stage.tone]}`}
                />
                <span className="truncate">{stage.label}</span>
                {interactive ? (
                  <Icon
                    name="chevron-right"
                    size={14}
                    className="text-text-muted ease-standard ml-auto shrink-0 opacity-0 transition duration-150 group-hover:opacity-100 motion-safe:group-hover:translate-x-0.5"
                  />
                ) : null}
              </p>
              {/* `tabular-nums` so a figure ticking from 9 to 10 does not shuffle its neighbours. */}
              <p className="text-text-primary m-0 mt-1 text-4xl font-semibold tabular-nums leading-none tracking-tight">
                {stage.value === null ? (
                  <>
                    <span aria-hidden="true">—</span>
                    <span className="visually-hidden">not yet available</span>
                  </>
                ) : (
                  stage.value
                )}
              </p>
              <p className="text-text-muted m-0 mt-1.5 text-xs">{stage.hint}</p>
              {stage.link}
            </div>
          )
        })}
      </div>

      {caption ? <div className="text-text-muted mt-4 text-xs">{caption}</div> : null}
    </div>
  )
}

export type ActionCardProps = {
  readonly icon: IconName
  readonly title: string
  readonly body: string
  /** Where it goes. Rendered by the caller's router: pass a `Link` or an `a`. */
  readonly children?: ReactNode
  readonly disabled?: boolean
  /** Why it is disabled, shown in place of the action. */
  readonly disabledReason?: string
  /** Tints the icon chip, so a row of quick actions is scannable by colour as well as by word. */
  readonly tone?: Tone
}

/**
 * A quick-action tile: icon, title, one sentence, one link. The `children` slot is the link so the
 * design system stays router-agnostic; the disabled form states its reason, because a control that
 * is off with no explanation reads as broken (ux.md §12, dead-end ban).
 *
 * Laid out HORIZONTALLY — chip beside the words, not above them. Stacked, four of these ran to
 * roughly 460 px of a dashboard's most valuable vertical space to carry sixty words, and compact is
 * the DEFAULT density for a sender surface for a reason (tokens.css: the target is speed of daily
 * use, not marketing airiness). Beside, the same four fit in half that, and the eye reads
 * chip → title → link in one sweep instead of three.
 */
export function ActionCard({
  icon,
  title,
  body,
  children,
  disabled = false,
  disabledReason,
  tone = 'accent',
}: ActionCardProps) {
  return (
    <div
      className={`border-card-border bg-card-bg shadow-raised ease-standard focus-within:border-accent group flex gap-3 rounded-xl border p-4 transition duration-150 ${
        disabled
          ? 'opacity-70'
          : 'hover:border-border-strong hover:shadow-lifted motion-safe:hover:-translate-y-0.5'
      }`}
      aria-disabled={disabled || undefined}
    >
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${
          disabled ? 'bg-surface-sunken text-text-muted' : CHIP_TONE[tone]
        }`}
      >
        <Icon name={icon} size={20} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-text-primary m-0 font-medium">{title}</p>
        <p className="text-text-muted m-0 mt-0.5 flex-1 text-sm">{body}</p>
        <div className="mt-2 text-sm font-medium">
          {disabled ? <span className="text-text-muted">{disabledReason}</span> : children}
        </div>
      </div>
    </div>
  )
}

export type MeterProps = {
  /** How much of the allowance is used. `null` is unknown, and draws no arc at all. */
  readonly value: number | null
  /** The ceiling. `null` means there is none, which is also drawn as no arc — nothing to fill. */
  readonly max: number | null
  /** What the ring measures, for a screen reader. The visible figure beside it is decorative here. */
  readonly label: string
  readonly tone?: 'accent' | 'warning' | 'success' | 'danger'
  readonly size?: 64 | 80 | 96
  /** Drawn in the hole: the figure itself, so the ring never has to carry the number as an image. */
  readonly children?: ReactNode
}

const METER_TONE: Record<NonNullable<MeterProps['tone']>, string> = {
  accent: 'text-accent',
  warning: 'text-warning',
  success: 'text-success',
  danger: 'text-danger',
}

/**
 * A usage ring.
 *
 * Deliberately not a chart. It answers one question — how much of the month is spent — at a glance
 * and from across a desk, which a bar of the same information does not. The figure it surrounds is
 * real DOM text rather than an SVG `<text>`, so it is selectable, it scales with the reader's font
 * size, and a test can assert the number rather than a path.
 *
 * An UNKNOWN value draws no arc. A ring at zero and a ring that could not be measured must not look
 * alike, for exactly the reason the tiles render a dash instead of a nought (ux.md §6.1).
 */
export function Meter({ value, max, label, tone = 'accent', size = 80, children }: MeterProps) {
  const known = value !== null && max !== null && max > 0
  // Clamped: over-allowance fills the ring rather than winding past the top, which would read as
  // less used rather than more.
  const fraction = known ? Math.min(1, Math.max(0, value / max)) : 0
  const stroke = size <= 64 ? 6 : 8
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={label}
        // -90° so the arc starts at twelve o'clock, where a person expects a dial to start.
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="text-border-subtle stroke-current"
        />
        {known ? (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - fraction)}
            className={`${METER_TONE[tone]} ease-standard stroke-current transition-[stroke-dashoffset] duration-500`}
          />
        ) : null}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {children}
      </div>
    </div>
  )
}
